import { useMemo, useEffect } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { useFetchQuery } from '@atlassian/jira-relay-utils/src/services/use-fetch-query/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { ITEMS_ON_PAGE } from '../../constants/default-parameters.tsx';
import type { InwardRelationships, OutwardRelationships } from '../../model/types.tsx';
import type {
	gqlJiraJswComponentsTableComponentsQuery$data,
	gqlJiraJswComponentsTableComponentsQuery$variables,
} from './__generated__/gqlJiraJswComponentsTableComponentsQuery.graphql';
import { SEARCH_COMPASS_COMPONENTS_QUERY_AGG_RELAY } from './gql.tsx';
import type { CompassComponent, CompassComponentLink } from './types.tsx';

export const useGetCompassComponents = (componentAris: string[]) => {
	const cloudId = useCloudId();

	const { data, loading, error } = useFetchQuery<
		gqlJiraJswComponentsTableComponentsQuery$variables,
		gqlJiraJswComponentsTableComponentsQuery$data
	>(
		SEARCH_COMPASS_COMPONENTS_QUERY_AGG_RELAY,
		{
			cloudId,
			query: {
				first: ITEMS_ON_PAGE,
				fieldFilters: [
					{
						name: 'entityId',
						filter: {
							in: componentAris,
						},
					},
				],
			},
		},
		{ fetchPolicy: 'network-only' },
	);

	const components = useMemo(() => {
		if (data?.compass?.searchComponents?.__typename !== 'CompassSearchComponentConnection') {
			return [];
		}

		return (
			data?.compass?.searchComponents.nodes
				?.map(mapNodeToComponent)
				.filter((component): component is CompassComponent => component != null) ?? []
		);
	}, [data]);

	useEffect(() => {
		if (!loading && error) {
			fireErrorAnalytics({
				meta: {
					id: 'useGetCompassComponents',
					packageName: 'jiraProjectDirectoriesComponents',

					teamName: 'compass-lodestone',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	}, [loading, error]);

	return { components, loading, error };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapNodeToComponent(node: any): CompassComponent | null {
	if (!node || !node.component || node.__typename !== 'CompassSearchComponentResult') {
		return null;
	}
	const { component } = node;

	let inwardRelationships: InwardRelationships;
	let outwardRelationships: OutwardRelationships;

	if (component.inwardRelationships.__typename !== 'CompassRelationshipConnection') {
		inwardRelationships = {
			nodes: [],
			pageInfo: {
				hasNextPage: false,
			},
			error: component.inwardRelationships.message,
		};
	} else {
		inwardRelationships = component.inwardRelationships;
	}

	if (component.outwardRelationships.__typename !== 'CompassRelationshipConnection') {
		outwardRelationships = {
			nodes: [],
			pageInfo: {
				hasNextPage: false,
			},
			error: component.outwardRelationships.message,
		};
	} else {
		outwardRelationships = component.outwardRelationships;
	}

	const repositoryLinks =
		component.links?.reduce((acc: CompassComponentLink[], cur: CompassComponentLink) => {
			if (cur.type === 'REPOSITORY') {
				acc.push(cur);
			}

			return acc;
		}, []) ?? [];

	return {
		id: component.id,
		name: component.name,
		description: component.description,
		typeId: component.typeId,
		ownerId: component.ownerId,
		links: repositoryLinks,
		link: node.link,
		state: component.state,
		scorecardScores: component.scorecardScores,

		inwardRelationships,
		outwardRelationships,
	};
}
